import React, { useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { useTranslations } from "next-intl";

import { useCourier } from "@/context/Courier.context";
import { Button, EmptyData } from "@/components/common";
import PaymentTable from "./payment-form/payment-table";

import { useParcelsRequest } from "@/api/useParcelsRequest";
import { deliveryAllowedStatuses } from "@/utils/additional";
import { useMakeDeliverytRequest } from "@/api/useMakeDeliveryRequest";
import { useTheme } from "@/context/Theme.context";
import { Theme } from "@/enums";

const ArrivedParcelsCourierModal = ({
  closeModal,
}: {
  closeModal: () => void;
}) => {
  const t = useTranslations();
  const theme = useTheme();

  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [step, setStep] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: parcelsData, isLoading } = useParcelsRequest();
  const makeDeliveryRequest = useMakeDeliverytRequest();

  const { courierSteps } = useCourier();

  const handleSubmit = async (data: any) => {
    if (selectedOrderIds.length === 0 || isSubmitting) return;

    setIsSubmitting(true);

    try {
      await Promise.all(
        selectedOrderIds.map((id) =>
          makeDeliveryRequest({
            shipmentId: id,
            addressDeliveryId: parseInt(data?.addressDeliveryId?.id),
            deliveryOptionId:
              theme === Theme.GEORGIA || theme === Theme.UK
                ? 2
                : data?.deliveryOptionId?.id,
          }),
        ),
      );
      closeModal();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const parcelsList = useMemo(() => {
    if (!parcelsData?.data?.data?.length) {
      return [];
    }

    return parcelsData?.data?.data.filter(
      (item) =>
        deliveryAllowedStatuses.includes(item.commonStatusCode) &&
        (theme === Theme.GEORGIA ? item?.isPaid : true),
    );
  }, [parcelsData]);

  const handleSelectOrder = (id: number) => {
    if (!selectedOrderIds.includes(id)) {
      setSelectedOrderIds([...selectedOrderIds, id]);
    } else {
      setSelectedOrderIds(
        selectedOrderIds.filter((selectedId) => selectedId !== id),
      );
    }
  };

  const handleSelectAllOrder = () => {
    if (selectedOrderIds.length < parcelsList?.length) {
      setSelectedOrderIds(
        parcelsList?.map((parcel) => parcel.shipmentId) || [],
      );
    } else {
      setSelectedOrderIds([]);
    }
  };

  if (step === 1) {
    if (parcelsList.length === 0) {
      return <EmptyData title={t("noParcelsForDeliveryRequest")} />;
    }
    return (
      <>
        <PaymentTable
          parcelsData={parcelsList}
          isLoading={isLoading}
          selectedIds={selectedOrderIds}
          handleSelect={handleSelectOrder}
          handleSelectAll={handleSelectAllOrder}
        />
        <div className="border-1 my-[24px] border-b border-[#f3f3f3]" />
        <div className="flex flex-col items-center justify-between px-[35px] pb-[24px]">
          <Button
            disabled={selectedOrderIds.length === 0 || isLoading}
            onClick={() => {
              setStep(2);
            }}
          >
            {t("orderCourier")}
          </Button>
        </div>
      </>
    );
  }

  if (step === 2) {
    return (
      <Formik
        initialValues={{
          addressDeliveryId: null,
          deliveryOptionId:
            theme === Theme.GEORGIA || theme === Theme.UK ? 2 : null,
        }}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form>
            <div>{courierSteps.component}</div>
          </Form>
        )}
      </Formik>
    );
  }

  return null;
};

export default ArrivedParcelsCourierModal;
